import Image from 'next/image'
import { FC, useState, useRef, CSSProperties, useEffect } from 'react'

import generics from '@/utils/generics'
import { useStoreActions } from '@/store'

interface Props {
  testId: string
  buttonText: JSX.Element | string
  tooltipName: string
  buttonStyle?: CSSProperties
  className: string
  underline: boolean
  arrowPosition?: 'anchorCenter' | 'anchorLeft' | 'anchorRight'
  positionBelow?:
    | 'Tooltip-content--aboveTrigger'
    | 'Tooltip-content--belowTrigger'
  dataTest?: string
  fullTooltipClass?: string
  icon?: string
  iconAltText?: string
  responsivePosition?: boolean
  children: JSX.Element | JSX.Element[]
  agentId: string
  index: number
  text: string
}

const Tooltip: FC<Props> = ({
  testId,
  buttonText,
  tooltipName,
  buttonStyle,
  className,
  underline,
  arrowPosition = 'anchorCenter',
  positionBelow = 'Tooltip-content--aboveTrigger',
  dataTest,
  fullTooltipClass,
  icon,
  iconAltText,
  responsivePosition = true,
  children,
  agentId,
  index,
  text
}) => {
  let timeout: NodeJS.Timeout
  const [isShown, setIsShown] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState(positionBelow)
  const buttonEl = useRef<HTMLButtonElement>(null)

  const { onElementClickedTracking } = useStoreActions(
    actions => actions.tracking
  )

  useEffect(() => {
    if (isShown) {
      const elementClickedPayload = generics.buildElementClickedPayload(
        'link',
        agentId,
        (index + 1).toString(),
        'tooltip',
        text,
        'MODAL'
      )

      onElementClickedTracking(elementClickedPayload)
    }
  }, [isShown])

  useEffect(() => {
    // click outside to close tooltip
    const hideTooltip = (event: MouseEvent) => {
      if (!buttonEl.current?.contains(event.target as Node)) {
        setIsShown(false)
      }
    }
    document.body.addEventListener('click', hideTooltip)
    return () => document.body.removeEventListener('click', hideTooltip)
  }, [])

  const showTip = () => {
    timeout = setTimeout(() => {
      // adjust tooltip position automatically
      if (responsivePosition && buttonEl.current) {
        setTooltipPosition(
          (buttonEl.current as HTMLElement).getBoundingClientRect().top > 150
            ? 'Tooltip-content--aboveTrigger'
            : 'Tooltip-content--belowTrigger'
        )
      }
      setIsShown(true)
    }, 400)
  }
  const hideTip = () => {
    clearInterval(timeout)
    setIsShown(false)
  }

  return (
    <div
      className={`Tooltip ${fullTooltipClass}`}
      data-tooltip="container"
      data-tooltip-type="clickable"
      data-testid={dataTest}
    >
      <button
        data-testid={testId}
        className={`Tooltip-trigger ${
          underline ? 'Tooltip-trigger--underline' : ''
        } bg-transparent ${className}`}
        type="button"
        data-tooltip="trigger"
        aria-controls="tooltip-content"
        aria-expanded="false"
        style={buttonStyle}
        ref={buttonEl}
        onClick={() => setIsShown(!isShown)}
      >
        {icon && (
          <Image
            width={16}
            height={16}
            src={icon}
            alt={iconAltText}
            className="mr-2"
          />
        )}
        {buttonText}
      </button>

      {isShown && (
        <div
          className={`Tooltip-content Tooltip-content--${arrowPosition} z-layer-five ${tooltipPosition}`}
          id="tooltip-content"
          role="tooltip"
          aria-label={`${tooltipName} tooltip`}
          data-tooltip="content"
          aria-hidden="true"
          style={{ maxWidth: '72vw' }}
        >
          <div className="Tooltip-body">
            <div className="Tooltip-arrow"></div>
            {children}
          </div>
        </div>
      )}
    </div>
  )
}

export default Tooltip
